import { useFormContext } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { FieldType, LayerSchemaConfigProperty } from '@packages/database/types';
import { cn } from '@/lib/utils';

interface Props {
  fields: LayerSchemaConfigProperty[];
}

export const DynamicSchemaFields = ({ fields }: Props) => {
  const {
    formState: { errors },
  } = useFormContext();

  return (
    <div className="space-y-3">
      {fields.map((field) => (
        <FieldRow
          key={field.alias}
          field={field}
          error={errors[field.alias]?.message as string | undefined}
        />
      ))}
    </div>
  );
};

const FieldRow = ({
  field,
  error,
}: {
  field: LayerSchemaConfigProperty;
  error?: string;
}) => {
  const { register, setValue, watch } = useFormContext();
  const value = watch(field.alias);
  const label = field.fieldLabel ?? field.alias;

  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-foreground">
        {label}
        {field.isRequired && <span className="text-destructive ml-0.5">*</span>}
      </Label>

      {field.fieldType === FieldType.SELECT && field.fieldOptions ? (
        <Select
          value={value || field.fieldOptions.defaultValue || ''}
          onValueChange={(v) => setValue(field.alias, v, { shouldDirty: true })}
        >
          <SelectTrigger
            className={cn(
              'h-8 text-xs bg-background',
              error && 'border-destructive',
            )}
          >
            <SelectValue placeholder={`Select ${label}`} />
          </SelectTrigger>

          <SelectContent
            //@ts-ignore
            container={document.body}
            className="z-[99999]"
            position="popper"
            sideOffset={4}
          >
            {field.fieldOptions.options.map((opt) => (
              <SelectItem
                key={opt.value}
                value={opt.value.toString()}
                className="text-xs"
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : field.fieldType === FieldType.BOOLEAN ? (
        <div className="flex items-center gap-2">
          <Switch
            checked={!!value}
            onCheckedChange={(v) =>
              setValue(field.alias, v, { shouldDirty: true })
            }
          />
          <span className="text-xs text-muted-foreground">
            {value ? 'Yes' : 'No'}
          </span>
        </div>
      ) : field.fieldType === FieldType.TEXTAREA ? (
        <Textarea
          {...register(field.alias, { required: field.isRequired })}
          rows={3}
          placeholder={label}
          className={cn(
            'text-xs resize-none bg-background',
            error && 'border-destructive',
          )}
        />
      ) : field.fieldType === FieldType.NUMBER ? (
        <Input
          type="number"
          {...register(field.alias, {
            required: field.isRequired,
            valueAsNumber: true,
          })}
          placeholder="0"
          className={cn(
            'h-8 text-xs bg-background',
            error && 'border-destructive',
          )}
        />
      ) : field.fieldType === FieldType.DATE ? (
        <Input
          type="date"
          {...register(field.alias, { required: field.isRequired })}
          className={cn(
            'h-8 text-xs bg-background',
            error && 'border-destructive',
          )}
        />
      ) : (
        <Input
          type="text"
          {...register(field.alias, { required: field.isRequired })}
          placeholder={label}
          className={cn(
            'h-8 text-xs bg-background',
            error && 'border-destructive',
          )}
        />
      )}

      {error && <p className="text-[10px] text-destructive">{error}</p>}
    </div>
  );
};
